import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { getLibrary } from 'services/library/library';
import { getCourse } from 'services/course/getCourse';
import { getAsana } from 'services/asana/getAsana';
import { useLibraryAsanas } from 'services/asanaLibrary/asanaLibrary';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, Button, Checkbox, Badge, Skeleton, Chip } from '@mui/material';
import AssignAsanaLibrary from 'components/openDialog/AssignAsanaLibrary';
import CreateAsanaLibrary from 'components/openDialog/CreateAsana';
import { useNavigate } from 'react-router-dom';
import { ArrowRightOutlined } from '@mui/icons-material';
import toast from 'react-hot-toast';

const fallbackImage =
  'https://cdn.vectorstock.com/i/1000x1000/89/18/young-woman-practicing-yoga-twisting-asana-or-pose-vector-42248918.webp';

const getStatusProps = (status) => {
  switch (status) {
    case 1:
      return { label: 'Approved', color: 'success', bgColor: '#e8f5e9' };
    case 0:
      return { label: 'Pending', color: 'warning', bgColor: '#fff3e0' };
    case 2:
      return { label: 'Rejected', color: 'error', bgColor: '#ffebee' };
    default:
      return { label: 'Unknown', color: 'default', bgColor: '#f5f5f5' };
  }
};

function Library() {
  const coachId = Number(localStorage.getItem('userid'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState('');
  const [selectedAsanas, setSelectedAsanas] = useState([]);

  // Fetch library
  const {
    data: libraryData,
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['alllibrary', coachId],
    queryFn: () => getLibrary({ coachId }),
    retryOnMount: true
  });

  // Fetch captured asanas
  const { data: asanaData } = useQuery({
    queryKey: ['capturedAsanas', coachId],
    queryFn: () => getAsana({ coachId })
  });

  const libraryList = libraryData?.response || [];
  const asanaList = asanaData?.response || [];

  // Combine captured images
  const combinedAsanas = libraryList.map((libAsana) => {
    const captured = asanaList.find((a) => a.id === libAsana.id); // Updated to match id
    return {
      ...libAsana,
      image: captured?.image || libAsana.image,
      posturename: libAsana.posturename || libAsana.name
    };
  });

  // Fetch courses
  const { data: courseData } = useQuery({
    queryKey: ['courses'],
    queryFn: () => getCourse({ coachid: coachId })
  });

  const courseList = courseData?.response || [];

  // Assign mutation
  const { mutate } = useMutation({
    mutationFn: useLibraryAsanas,
    onSuccess: (response) => {
      if (response?.status) {
        toast.success(response.data.message || 'Asanas assigned to course successfully');
        queryClient.invalidateQueries(['alllibrary', coachId]);
        queryClient.invalidateQueries(['capturedAsanas', coachId]);
        setSelectedCourses('');
        setSelectedAsanas([]);
        setOpenAssignDialog(false);
        navigate('/library');
      } else {
        toast.error('Unexpected response format');
      }
    },
    onError: (error) => {
      console.error('Assign mutation error:', error);
      toast.error(error?.response?.data?.message || 'Failed to assign asanas');
    }
  });

  const handleToggleAsana = (asanaId) => {
    setSelectedAsanas((prev) => (prev.includes(asanaId) ? prev.filter((id) => id !== asanaId) : [...prev, asanaId]));
  };

  const handleAssignSubmit = () => {
    if (!selectedCourses || selectedAsanas.length === 0) {
      toast.error('Please select a course and at least one asana.');
      return;
    }

    const selectedAsanaObjects = libraryList.filter((a) => selectedAsanas.includes(a.id));
    const libraryDatas = selectedAsanaObjects.map((asana) => ({
      id: asana.id,
      coachid: coachId,
      name: asana.name || '',
      image: asana.image || null,
      status: asana.status ?? 0,
      created_at: asana.created_at || new Date().toISOString(),
      courseId: selectedCourses.courseId,
      landmarks: asana.landmarks || []
    }));

    console.log('Assigning asanas:', libraryDatas); // Debug log
    mutate(libraryDatas);
  };

  return (
    <Box sx={{ minHeight: '100vh', p: { xs: 2, md: 4 }, bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          px: { xs: 1, sm: 2 },
          py: 2,
          position: 'sticky',
          top: 61,
          zIndex: 1100,
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ color: '#1a237e' }}>
          🧘 Asana Library
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedAsanas.length > 0 && (
            <Button variant="contained" color="primary" onClick={() => setOpenAssignDialog(true)}>
              Assign {selectedAsanas.length} Asana{selectedAsanas.length > 1 ? 's' : ''}
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={() => setOpenCreateDialog(true)}>
            Create New Asana
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Grid container spacing={4}>
          {[...Array(6)].map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width="80%" sx={{ mt: 2 }} />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))}
        </Grid>
      ) : isError ? (
        <Box textAlign="center" py={6}>
          <Typography color="error" variant="h6" mb={2}>
            Failed to load asana library.
          </Typography>
          <Button variant="outlined" color="primary" onClick={() => refetch()} sx={{ textTransform: 'none', fontWeight: 'bold' }}>
            Retry
          </Button>
        </Box>
      ) : combinedAsanas.length === 0 ? (
        <Box textAlign="center" py={6}>
          <img src={fallbackImage} alt="No Data" style={{ width: 120, height: 120, margin: '0 auto 16px', borderRadius: 8 }} />
          <Typography variant="h6" color="text.secondary">
            No asanas found in your library.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateDialog(true)}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Create Your First Asana
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {combinedAsanas.map((asana, idx) => {
            const isSelected = selectedAsanas.includes(asana.id);
            const isCoachAsana = asana.coachid === coachId;
            const { label, color, bgColor } = getStatusProps(asana.status);
            const selectionOrder = isSelected ? selectedAsanas.indexOf(asana.id) + 1 : null;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asana.id}>
                <Card
                  sx={{
                    height: '100%',
                    boxShadow: isSelected ? '0 0 0 3px #1976d2' : '0 4px 12px rgba(0,0,0,0.1)',
                    cursor: isCoachAsana ? 'pointer' : 'default',
                    position: 'relative',
                    bgcolor: '#ffffff'
                  }}
                  onClick={() => isCoachAsana && handleToggleAsana(asana.id)}
                  role="button"
                  aria-label={`Select ${asana.posturename || asana.name || 'asana'}`}
                >
                  {isCoachAsana && (
                    <Badge
                      badgeContent={selectionOrder}
                      color="primary"
                      invisible={!isSelected}
                      sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}
                    />
                  )}
                  {isCoachAsana && (
                    <Checkbox
                      checked={isSelected}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        zIndex: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => handleToggleAsana(asana.id)}
                      aria-label={`Toggle selection for ${asana.posturename || asana.name || 'asana'}`}
                    />
                  )}
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      overflow: 'hidden',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8
                    }}
                  >
                    <img
                      src={asana.image || fallbackImage}
                      alt={asana.posturename || asana.name || 'Asana'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ textTransform: 'capitalize', color: '#1e293b', mb: 1 }}>
                      {asana.posturename || asana.name || 'Unknown Asana'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Created: {asana.created_at ? new Date(asana.created_at).toLocaleDateString() : 'N/A'}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: 1,
                        mb: 2,
                        pb: 1, // Extra padding for scrollbar
                        '&::-webkit-scrollbar': {
                          height: '6px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: '#bdbdbd',
                          borderRadius: '6px'
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      {asana.linkedCourses && asana.linkedCourses.length > 0 ? (
                        <>
                          {asana.linkedCourses.slice(0, 3).map((courseName, idx) => (
                            <Chip
                              key={idx}
                              label={courseName}
                              size="small"
                              clickable
                              onClick={(e) => {
                                e.stopPropagation();
                                const course = courseList.find((c) => c.courseName === courseName);
                                if (course) {
                                  navigate(`/view-asana/${course.courseId}`);
                                } else {
                                  toast.error('Course not found');
                                }
                              }}
                              sx={{
                                bgcolor: '#e3f2fd',
                                color: '#1976d2',
                                fontWeight: 'medium',
                                '&:hover': { bgcolor: '#bbdefb' },
                                flexShrink: 0 // Prevent chip from shrinking
                              }}
                              aria-label={`View course ${courseName}`}
                            />
                          ))}
                          {asana.linkedCourses.length > 3 && (
                            <Chip
                              label={`+${asana.linkedCourses.length - 3} more`}
                              size="small"
                              clickable
                              onClick={(e) => {
                                e.stopPropagation();
                                // Optionally open a tooltip, modal, or navigate to a detailed view
                                toast.success(`Additional courses: ${asana.linkedCourses.slice(3).join(', ')}`);
                              }}
                              sx={{
                                bgcolor: '#f5f5f5',
                                color: '#616161',
                                fontWeight: 'medium',
                                '&:hover': { bgcolor: '#e0e0e0' },
                                flexShrink: 0
                              }}
                              aria-label={`View additional courses`}
                            />
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not assigned to any course
                        </Typography>
                      )}
                    </Box>
                    {isCoachAsana && (
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            flex: 1,
                            fontWeight: 'bold',
                            textTransform: 'none',
                            py: 1,
                            bgcolor: asana.image ? '#1976d2' : '#2e7d32',
                            '&:hover': { bgcolor: asana.image ? '#115293' : '#1b5e20' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            localStorage.setItem('asanaId', asana.id);
                            const navigationState = {
                              posture: {
                                postureId: asana.id,
                                posturename: asana.posturename || asana.name,
                                posturenumber: asana.posturenumber,
                                landmarks: asana.landmarks ? JSON.parse(asana.landmarks) : [],
                                courseId: asana.courseId,
                                status: asana.status,
                                image: asana.image
                              },
                              source: 'library'
                            };
                            navigate('/create-pose', { state: navigationState });
                          }}
                        >
                          {asana.image ? 'Re-Train' : 'Train'}
                          <ArrowRightOutlined style={{ marginLeft: 8 }} />
                        </Button>
                        {asana.image && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{
                              flex: 1,
                              fontWeight: 'bold',
                              textTransform: 'none',
                              py: 1,
                              borderColor: '#1976d2',
                              '&:hover': { borderColor: '#115293', bgcolor: '#e3f2fd' }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(asana.image.startsWith('http') ? asana.image : `http://localhost:5000/${asana.image}`, '_blank');
                            }}
                          >
                            View
                          </Button>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AssignAsanaLibrary
        open={openAssignDialog}
        onClose={() => {
          setOpenAssignDialog(false);
          setSelectedCourses('');
        }}
        courseList={courseList}
        selectedCourses={selectedCourses}
        setSelectedCourses={setSelectedCourses}
        onAssign={handleAssignSubmit}
      />

      <CreateAsanaLibrary
        open={openCreateDialog}
        onClose={() => {
          setOpenCreateDialog(false);
          setSelectedCourses('');
        }}
      />
    </Box>
  );
}

export default Library;
